import {
  ArrowsClockwiseIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  CopyIcon,
  CreditCardIcon,
  FingerprintIcon,
  IdentificationCardIcon,
  InfoIcon,
  PackageIcon,
  ReceiptIcon,
  SparkleIcon,
  TagIcon,
  UsersIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Badge, Button, Card, Label, Radio } from 'flowbite-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AnimatedModal from '../../components/AnimatedModal';
import StatCard from '../../components/StatCard';
import { useAuth, useCancelSubscription, useSubscriptionDetails, useTeamOverview } from '../../hooks';
import { BillingSkeleton } from '../../components/SkeletonLoaders';

/** Returns formatted date or "—" for null/undefined/invalid */
const safeDate = (value: string | null | undefined, opts?: Intl.DateTimeFormatOptions): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, opts ?? { day: 'numeric', month: 'short', year: 'numeric' });
};

const Billing = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: details, isLoading: isLoadingDetails } = useSubscriptionDetails();
  const { data: teamOverview, isLoading: isLoadingOverview } = useTeamOverview();
  const cancelMutation = useCancelSubscription();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);

  const isLoading = isLoadingDetails || (user?.is_team_admin && isLoadingOverview);

  const handleOpenCancelModal = () => {
    if (details?.cancel_at_period_end) {
      setCancelImmediate(true);
    } else {
      setCancelImmediate(false);
    }
    setShowCancelModal(true);
  };

  const handleCancelSubscription = async () => {
    cancelMutation.mutate({ immediate: cancelImmediate }, {
      onSuccess: (response) => {
        toast.success(response.message || t('billing.cancelSuccess', 'Subscription updated'));
        setShowCancelModal(false);
      },
      onError: (error: Error) => {
        toast.error(error?.message || t('billing.cancelError', 'Failed to cancel subscription'));
      }
    });
  };

  const getStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <Badge color="success" icon={CheckCircleIcon}>{t('billing.status.active', 'Active')}</Badge>;
      case 'trialing':
        return <Badge color="indigo" icon={SparkleIcon}>{t('billing.status.trialing', 'Trialing')}</Badge>;
      case 'past_due':
        return <Badge color="warning" icon={WarningIcon}>{t('billing.status.pastDue', 'Past Due')}</Badge>;
      case 'canceled':
      case 'unpaid':
        return <Badge color="failure" icon={XCircleIcon}>{t('billing.status.canceled', 'Canceled')}</Badge>;
      default:
        return <Badge color="gray">{t('billing.status.noSubscription', 'No Active Subscription')}</Badge>;
    }
  };

  if (isLoading) {
    return <BillingSkeleton />;
  }

  return (
    <div>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
        <div className="flex items-center gap-2 text-gray-700">
          <ReceiptIcon size={24} weight="duotone" />
          <h1 className="text-xl font-bold">
            {t('billing.title', 'Billing & Subscription')}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            color="primary"
            size="md"
            onClick={() => navigate('/billing-history')}
            className="rounded-xl border-gray-100 shadow-sm"
          >
            <ClockIcon size={18} className="mr-2" />
            {t('billing.paymentHistory', 'Payment History')}
          </Button>
        </div>
      </header>

      {user?.is_team_admin && teamOverview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <StatCard
            title={t('billing.teamMembers', 'Team Members')}
            value={teamOverview.total_team_members}
            icon={<UsersIcon size={24} weight="duotone" />}
            accentColor="indigo"
          />
          <StatCard
            title={t('billing.totalSpent', 'Total Spent')}
            value={parseFloat(teamOverview.total_spent)}
            isCurrency={true}
            icon={<CreditCardIcon size={24} weight="duotone" />}
            accentColor="green"
          />
          <StatCard
            title={t('billing.upcomingCharge', 'Upcoming Charge')}
            value={parseFloat(teamOverview.upcoming_charges)}
            isCurrency={true}
            icon={<CalendarIcon size={24} weight="duotone" />}
            accentColor="amber"
          />
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
          {t('billing.currentPlan', 'Subscription Details')}
        </h2>
        <Card theme={{
          root: {
            children: "flex h-full flex-col p-4 gap-0"
          }
        }}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="p-4 bg-indigo-50 rounded-2xl text-indigo-600">
                <PackageIcon size={40} weight="duotone" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900">
                    {details?.plan?.name || t('billing.noPlan', 'Free Tier')}
                  </h3>
                  {getStatusBadge(details?.subscription_status)}
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  {details?.plan
                    ? `${details.plan.currency === 'INR' ? '₹' : details.plan.currency}${parseFloat(details.plan.amount).toLocaleString()} / ${details.plan.interval}`
                    : t('billing.freeDescription', 'Basic features included')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              {details?.subscription_status === 'active' && user?.is_team_admin && (
                <Button color="light" size="sm" onClick={handleOpenCancelModal} className="flex-1 md:flex-none">
                  {t('billing.cancel', 'Cancel')}
                </Button>
              )}
              <Button color="primary" onClick={() => navigate('/pricing')} className="flex-1 md:flex-none">
                {details?.plan ? t('billing.changePlan', 'Change Plan') : t('billing.subscribe', 'Subscribe Now')}
              </Button>
            </div>
          </div>

          {details?.is_in_trial && (
            <div className="mt-4 p-4 bg-indigo-50/50 border border-indigo-100 rounded-xl flex items-center gap-3 text-sm text-indigo-700">
              <SparkleIcon size={20} weight="fill" />
              <div>
                <p className="font-semibold">{t('billing.trialActive', 'Free Trial Active')}</p>
                <p className="text-xs opacity-80">
                  {t('billing.trialRemaining', 'Your trial ends in {{days}} days ({{date}}).', {
                    days: details.days_until_trial_end,
                    date: safeDate(details.trial_end),
                  })}
                </p>
              </div>
            </div>
          )}

          {details?.cancel_at_period_end && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-center gap-3 text-sm text-amber-700">
              <InfoIcon size={20} weight="fill" />
              <div>
                <p className="font-semibold">{t('billing.cancellationPending', 'Cancellation Pending')}</p>
                <p className="text-xs opacity-80">
                  {t('billing.cancelDate', 'Subscription will cancel automatically on {{date}}.', {
                    date: safeDate(details.current_period_end),
                  })}
                </p>
              </div>
            </div>
          )}

          {details && (
            <div className="mt-8 border-t border-gray-100 pt-8">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 px-1 flex items-center gap-2">
                <IdentificationCardIcon size={18} className="text-gray-400" />
                {t('billing.managementDetails', 'Management Details')}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-12 px-1">
                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <FingerprintIcon size={12} className="text-indigo-400" />
                    {t('billing.customerId', 'Customer ID')}
                  </p>
                  <div
                    className="flex items-center gap-2 group cursor-pointer active:scale-95 transition-transform"
                    onClick={() => {
                      navigator.clipboard.writeText(details.stripe_customer_id);
                      toast.success(t('common.copied', 'Copied to clipboard'));
                    }}
                  >
                    <code className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100 truncate flex-grow">
                      {details.stripe_customer_id}
                    </code>
                    <CopyIcon size={14} className="text-gray-300 group-hover:text-indigo-500 transition-colors" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <TagIcon size={12} className="text-emerald-400" />
                    {t('billing.subscriptionId', 'Subscription ID')}
                  </p>
                  <div
                    className="flex items-center gap-2 group cursor-pointer active:scale-95 transition-transform"
                    onClick={() => {
                      navigator.clipboard.writeText(details.stripe_subscription_id);
                      toast.success(t('common.copied', 'Copied to clipboard'));
                    }}
                  >
                    <code className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100 truncate flex-grow">
                      {details.stripe_subscription_id}
                    </code>
                    <CopyIcon size={14} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <CalendarIcon size={12} className="text-blue-400" />
                    {t('billing.memberSince', 'Member Since')}
                  </p>
                  <p className="text-sm font-bold text-gray-700">
                    {safeDate(details.current_period_start)}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <ClockIcon size={12} className="text-amber-400" />
                    {t('billing.accountAge', 'Account Age')}
                  </p>
                  <p className="text-sm font-bold text-gray-700">
                    {details.subscription_age_days} {t('common.days', 'days')}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <ArrowsClockwiseIcon size={12} className="text-purple-400" />
                    {t('billing.nextPayment', 'Next Payment In')}
                  </p>
                  <p className="text-sm font-bold text-gray-700">
                    {details.days_until_next_payment ?? 0} {t('common.days', 'days')}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <CreditCardIcon size={12} className="text-rose-400" />
                    {t('billing.billingEmail', 'Billing Email')}
                  </p>
                  <p className="text-sm font-bold text-gray-700 truncate" title={details.email}>
                    {details.email}
                  </p>
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <InfoIcon size={12} className="text-teal-400" />
                    {t('billing.servicePeriod', 'Service Period')}
                  </p>
                  <div className="flex items-center gap-3 text-sm font-bold text-gray-700">
                    <span>{safeDate(details.current_period_start)}</span>
                    <span className="text-gray-300 font-normal">→</span>
                    <span>{safeDate(details.current_period_end)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Plan Limits */}
          {details?.package_limits && (
            <div className="mt-8 border-t border-gray-100 pt-8">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 px-1 flex items-center gap-2">
                <PackageIcon size={18} className="text-gray-400" />
                {t('billing.planLimits', 'Plan Limits')}
                {details.package && (
                  <span className="ml-auto text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    {details.package}
                  </span>
                )}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-12 px-1">
                {([
                  { key: "max_apps" as const, label: t('billing.maxApps', 'Max Apps'), icon: <SparkleIcon size={12} className="text-indigo-400" /> },
                  { key: "max_team_seats" as const, label: t('billing.maxSeats', 'Max Team Seats'), icon: <UsersIcon size={12} className="text-blue-400" /> },
                  { key: "initial_data_pull_days" as const, label: t('billing.dataPullDays', 'Data Pull Days'), icon: <ArrowsClockwiseIcon size={12} className="text-emerald-400" /> },
                  { key: "live_dashboard_window_days" as const, label: t('billing.dashboardWindow', 'Dashboard Window'), icon: <CalendarIcon size={12} className="text-amber-400" /> },
                ]).map(({ key, label, icon }) => {
                  const limits = details.package_limits!;
                  const value = limits[key];
                  return (
                    <div key={key} className="space-y-1.5">
                      <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                        {icon}
                        {label}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-gray-700 dark:text-gray-200">
                          {value === -1 ? "Unlimited" : value}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </section>

      {/* Cancellation Modal */}
      <AnimatedModal
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        icon={WarningIcon}
        isConfirmation
        confirmText="Confirm Cancellation"
        confirmButtonColor="red"
        confirmationText="CANCEL"
        isConfirming={cancelMutation.isPending}
        onConfirm={handleCancelSubscription}
        description={t('billing.cancelWarning', 'Are you sure you want to cancel your subscription? This action cannot be undone.')}
      >
        <div className="space-y-4 mt-2">
          <fieldset className="flex flex-col gap-3">
            {!details?.cancel_at_period_end && (
              <div
                className={`flex items-start gap-3 p-3.5 rounded-2xl border-2 transition-all cursor-pointer ${!cancelImmediate ? 'border-indigo-600 bg-indigo-50/30 shadow-sm' : 'border-gray-100 bg-gray-50/20 hover:bg-gray-50/40'}`}
                onClick={() => setCancelImmediate(false)}
              >
                <div className="mt-1">
                  <Radio
                    id="period-end"
                    name="cancelType"
                    value="false"
                    checked={!cancelImmediate}
                    onChange={() => setCancelImmediate(false)}
                  />
                </div>
                <Label htmlFor="period-end" className="flex flex-col cursor-pointer flex-1">
                  <span className="font-bold text-gray-900 text-base leading-tight">{t('billing.cancelAtEnd', 'Cancel at end of billing period')}</span>
                  <span className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                    {t('billing.cancelAtEndDesc', "Keep access until the end of your current period. You won't be charged again.")}
                  </span>
                </Label>
              </div>
            )}

            <div
              className={`flex items-start gap-3 p-3.5 rounded-2xl border-2 transition-all cursor-pointer ${cancelImmediate ? 'border-red-600 bg-red-50/30 shadow-sm' : 'border-gray-100 bg-gray-50/20 hover:bg-gray-50/40'}`}
              onClick={() => setCancelImmediate(true)}
            >
              <div className="mt-1">
                <Radio
                  id="immediate"
                  name="cancelType"
                  value="true"
                  checked={cancelImmediate}
                  onChange={() => setCancelImmediate(true)}
                />
              </div>
              <Label htmlFor="immediate" className="flex flex-col cursor-pointer flex-1">
                <span className="font-bold text-red-600 text-base leading-tight">{t('billing.cancelImmediate', 'Cancel immediately')}</span>
                <span className="text-[11px] text-red-400 mt-1 leading-relaxed">
                  {t('billing.cancelImmediateDesc', 'Lose access to all features instantly. No refunds are provided for the remaining time.')}
                </span>
              </Label>
            </div>
          </fieldset>

          {details?.cancel_at_period_end && (
            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-100/50 flex gap-2.5 items-start">
              <div className="bg-amber-100 p-1.5 rounded-lg shrink-0">
                <WarningIcon size={14} className="text-amber-600" weight="fill" />
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-amber-800 leading-none mb-1">
                  {t('billing.alreadyPending', 'Cancellation Already Pending')}
                </p>
                <p className="text-[10px] text-amber-700 leading-relaxed font-medium">
                  {t('billing.alreadyPendingWarning', 'Important: Your subscription is already scheduled for cancellation. This action will terminate your access immediately.')}
                </p>
              </div>
            </div>
          )}
        </div>
      </AnimatedModal>
    </div>
  );
};

export default Billing;
